/* Showcase carousel — <mw-showcase> for sections/showcase-carousel.liquid.

   Endless centre-focus carousel without a library. All slides share one grid cell; each gets
   --d = its distance from the centred slide in slide widths (…, -1, 0, 1, …) and CSS moves it
   there. The ring is endless: a slide that has to change sides jumps without a transition while
   it is off-screen. Fewer than five slides are cloned (aria-hidden, inert) so a slide is always
   free to make that jump out of sight.

   Input: arrow buttons (desktop mouse: the arrow follows the pointer over the images and the
   native cursor is hidden — Helix); ←/→ keys inside the carousel; swipe on touch; a tap or
   click on a neighbour centres it; selecting a block in the theme editor centres it at once.
   The centred slide's colour scheme / custom background is handed to the section box (boxed)
   or the section. Block entrances ([data-anim], motion.css) replay whenever a slide is centred.
   Reduced motion: CSS drops every transition; entrances are not replayed.

   Event: `mw:showcase:change` (bubbles) with detail { index } after every change. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const followPointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1025px)');
const MIN_RING = 5; // centre, two neighbours and one hidden spare on each side
const SWIPE_MIN = 40; // px of horizontal travel before a swipe counts
const DRAG_START = 8; // px before a touch is treated as a horizontal drag

class MwShowcase extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    this.originals = [...this.track.querySelectorAll('[data-slide]')].filter(
      (slide) => slide.parentElement.closest('[data-slide]') === null
    );
    this.count = this.originals.length;
    if (this.count === 0) return;

    this.buildRing();
    this.index = 0;
    this.label();
    this.findTarget();

    this.abort = new AbortController();
    const on = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, { ...options, signal: this.abort.signal });

    const single = this.count < 2;
    this.classList.toggle('is-single', single);
    this.querySelector('[data-nav]')?.toggleAttribute('hidden', single);

    if (!single) {
      this.querySelectorAll('[data-prev]').forEach((button) => on(button, 'click', () => this.go(this.index - 1)));
      this.querySelectorAll('[data-next]').forEach((button) => on(button, 'click', () => this.go(this.index + 1)));
      on(this, 'keydown', (event) => this.onKey(event));
      on(this.track, 'click', (event) => this.onTrackClick(event));
      on(this.track, 'pointerdown', (event) => this.onDown(event));
      on(this, 'pointermove', (event) => this.onHover(event), { passive: true });
      on(this, 'pointerleave', () => this.unfollow());
      on(window, 'scroll', () => this.unfollow(), { passive: true });
      on(followPointer, 'change', () => this.unfollow());
    }
    on(document, 'shopify:block:select', (event) => this.onEditor(event));

    this.resizeObserver = new ResizeObserver(() => this.placeNav());
    this.resizeObserver.observe(this);

    this.classList.add('is-ready');
    this.render(false);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.hoverFrame);
  }

  /* ---------- Ring ---------- */

  buildRing() {
    this.slides = [...this.originals];
    if (this.count < 2) return;
    const copies = Math.ceil(MIN_RING / this.count) - 1;
    for (let copy = 0; copy < copies; copy += 1) {
      this.originals.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach((el) => {
          el.removeAttribute('id');
          el.removeAttribute('data-shopify-editor-block');
        });
        this.track.append(clone);
        this.slides.push(clone);
      });
    }
  }

  label() {
    const template = this.dataset.slideLabel || '[index] / [count]';
    const role = this.dataset.slideRole || 'slide';
    this.originals.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', role);
      slide.setAttribute('aria-label', template.replace('[index]', i + 1).replace('[count]', this.count));
    });
  }

  offset(i) {
    const size = this.slides.length;
    let d = (((i - this.index) % size) + size) % size;
    if (d > size / 2) d -= size;
    return d;
  }

  /* ---------- Moving ---------- */

  go(target, animate = true) {
    if (this.slides.length < 2) return;
    const size = this.slides.length;
    const step = target - this.index;
    this.index = ((target % size) + size) % size;
    this.render(animate, step);
    this.dispatchEvent(new CustomEvent('mw:showcase:change', { bubbles: true, detail: { index: this.index % this.count } }));
  }

  render(animate, step = 0) {
    const jumping = [];
    this.slides.forEach((slide, i) => {
      const d = this.offset(i);
      const previous = slide.mwD;
      // A slide that changes sides of the ring jumps (while off-screen) instead of sliding across.
      if (!animate || previous === undefined || Math.abs(d - previous) > Math.abs(step)) jumping.push(slide);
      slide.mwD = d;
      slide.style.setProperty('--d', d);
    });

    if (jumping.length) {
      jumping.forEach((slide) => slide.classList.add('is-jumping'));
      void this.track.offsetWidth;
      jumping.forEach((slide) => slide.classList.remove('is-jumping'));
    }

    const active = this.slides[this.index];
    this.slides.forEach((slide) => {
      const isActive = slide === active;
      const wasActive = slide.classList.contains('is-active');
      slide.classList.toggle('is-active', isActive);
      const content = slide.querySelector('.showcase__content');
      if (content && !slide.hasAttribute('data-clone')) content.inert = !isActive;
      // The first centred slide is revealed by motion.js when the section scrolls into view.
      if (isActive && !wasActive && this.rendered) this.replay(slide);
    });
    this.rendered = true;

    this.applyScheme(active);
    this.placeNav();
  }

  /* Block entrances (motion.css) play again when a slide is centred. motion.js reveals every
     [data-anim] once; here the reveal is withdrawn without a transition and given back. */
  replay(slide) {
    const items = [...slide.querySelectorAll('[data-anim]')];
    if (!items.length) return;
    const root = document.documentElement;
    if (!root.classList.contains('anim-on') || reducedMotion.matches) {
      items.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    slide.classList.add('is-replaying');
    items.forEach((el) => el.classList.remove('is-inview', 'anim-done'));
    void slide.offsetWidth;
    slide.classList.remove('is-replaying');
    requestAnimationFrame(() => {
      items.forEach((el) => el.classList.add('is-inview'));
    });
  }

  /* ---------- Colour hand-over ---------- */

  findTarget() {
    const section = this.closest('.section');
    this.target = section?.classList.contains('section--boxed') ? this.closest('.section__box') : section;
    if (!this.target) return;
    this.baseScheme = [...this.target.classList].find((name) => name.startsWith('color-')) || '';
  }

  applyScheme(slide) {
    if (!this.target) return;
    const scheme = slide.dataset.scheme || this.baseScheme;
    [...this.target.classList]
      .filter((name) => name.startsWith('color-') && name !== scheme)
      .forEach((name) => this.target.classList.remove(name));
    if (scheme) this.target.classList.add(scheme);
    if (slide.dataset.customBg) this.target.style.setProperty('background', slide.dataset.customBg);
    else this.target.style.removeProperty('background');
  }

  /* ---------- Arrows ---------- */

  // Keyboard position: next to the centred slide, level with the middle of its media.
  placeNav() {
    const media = this.slides?.[this.index]?.querySelector('[data-media]');
    if (!media) return;
    const box = this.getBoundingClientRect();
    const rect = media.getBoundingClientRect();
    this.style.setProperty('--sc-nav-y', `${Math.round(rect.top - box.top + rect.height / 2)}px`);
  }

  onHover(event) {
    if (event.pointerType !== 'mouse' || !followPointer.matches) return;
    const { clientX, clientY } = event;
    cancelAnimationFrame(this.hoverFrame);
    this.hoverFrame = requestAnimationFrame(() => {
      if (!this.overMedia(clientX, clientY)) {
        this.unfollow();
        return;
      }
      const box = this.getBoundingClientRect();
      const x = clientX - box.left;
      this.style.setProperty('--sc-cx', `${Math.round(x)}px`);
      this.style.setProperty('--sc-cy', `${Math.round(clientY - box.top)}px`);
      this.dataset.follow = x < box.width / 2 ? 'prev' : 'next';
      this.classList.add('is-following');
    });
  }

  overMedia(x, y) {
    const box = this.getBoundingClientRect();
    if (x < box.left || x > box.right) return false;
    return this.slides.some((slide) => {
      if (Math.abs(slide.mwD) > 1) return false;
      const rect = slide.querySelector('[data-media]')?.getBoundingClientRect();
      return rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    });
  }

  unfollow() {
    cancelAnimationFrame(this.hoverFrame);
    this.classList.remove('is-following');
    delete this.dataset.follow;
  }

  /* ---------- Keyboard, clicks, swipe ---------- */

  onKey(event) {
    if (event.target.closest('input, textarea, select, [contenteditable]')) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index + (rtl ? 1 : -1));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.index + (rtl ? -1 : 1));
    }
  }

  onTrackClick(event) {
    if (this.suppressClick) {
      this.suppressClick = false;
      event.preventDefault();
      return;
    }
    const slide = event.target.closest('[data-slide]');
    if (!slide || slide.classList.contains('is-active')) return;
    event.preventDefault();
    this.go(this.index + slide.mwD);
  }

  onDown(event) {
    if (event.pointerType === 'mouse' || !event.isPrimary) return;
    const startX = event.clientX;
    const startY = event.clientY;
    let dx = 0;
    let dragging = false;
    const signal = this.abort.signal;
    const moves = new AbortController();
    const stop = () => moves.abort();
    signal.addEventListener('abort', stop, { once: true });

    const move = (e) => {
      if (e.pointerId !== event.pointerId) return;
      dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!dragging) {
        if (Math.abs(dy) > DRAG_START && Math.abs(dy) > Math.abs(dx)) return stop();
        if (Math.abs(dx) < DRAG_START) return;
        dragging = true;
        this.classList.add('is-dragging');
      }
      this.track.style.setProperty('--drag', `${Math.round(dx)}px`);
    };
    const end = (e) => {
      if (e.pointerId !== event.pointerId) return;
      stop();
      if (!dragging) return;
      this.classList.remove('is-dragging');
      this.track.style.removeProperty('--drag');
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false), 400);
      if (Math.abs(dx) >= SWIPE_MIN) this.go(this.index + (dx < 0 ? 1 : -1));
    };
    window.addEventListener('pointermove', move, { passive: true, signal: moves.signal });
    window.addEventListener('pointerup', end, { signal: moves.signal });
    window.addEventListener('pointercancel', end, { signal: moves.signal });
  }

  /* ---------- Theme editor ---------- */

  onEditor(event) {
    const slide = event.target?.closest?.('[data-slide]');
    if (!slide || !this.contains(slide)) return;
    const i = this.slides.indexOf(slide);
    if (i === -1) return;
    this.index = i;
    this.render(false);
  }
}

if (!customElements.get('mw-showcase')) customElements.define('mw-showcase', MwShowcase);
